import type { SiteConfig } from '@/types'
import { getOrigin } from '@/lib/window'

export const siteConfig: SiteConfig = {
    name: 'Prakrit Suvaani',
    description: 'Learn Prakrit from zero: first word, first sentence, then stories, with every form beside its Hindi and English meaning and checked by a teacher.',
    placeholder: '',
    url: getOrigin()
}

export const BRAND = {
    name: 'Suvaani',
    wordmark: 'Suvaani',
    tagline: { en: 'Learn Prakrit', hi: 'प्राकृत सीखें' }
}

/**
 * Whether the picture files are in public/images. This copy of the site was made without them, so every picture
 * (illustrations, word and root pictures, story covers, logos, screenshots) renders a placeholder of the same size
 * and shape through `@/components/generals/site-image`, and share tags and home-screen icons fall back to
 * /favicon.svg. Set this to true once the files are back in public/images (and on the image CDN, see CLAUDE.md).
 */
export const IMAGES_AVAILABLE = false

export type SocialId = 'linkedin' | 'instagram' | 'youtube' | 'facebook' | 'x'

export interface SocialLink {
    id: SocialId
    label: string
    href: string
}

/**
 * Suvaani's social profiles, shown in both footers. PLACEHOLDERS: each still points at the network's own home page.
 * Replace every href with the real profile address; once they are real, also add them to the Organization `sameAs`
 * on the landing page so search engines connect the profiles to Suvaani.
 */
export const SOCIAL_LINKS: SocialLink[] = [
    { id: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/' },
    { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/' },
    { id: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/' },
    { id: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/' },
    { id: 'x', label: 'X', href: 'https://x.com/' }
]

/**
 * Each course's own profiles, shown in that course's footer (no LinkedIn). PLACEHOLDERS as well: replace each href
 * with the course's real profile address.
 */
export const COURSE_SOCIAL_LINKS: Record<'prakrit' | 'sanskrit', SocialLink[]> = {
    prakrit: [
        { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/' },
        { id: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/' },
        { id: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/' },
        { id: 'x', label: 'X', href: 'https://x.com/' }
    ],
    sanskrit: [
        { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/' },
        { id: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/' },
        { id: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/' },
        { id: 'x', label: 'X', href: 'https://x.com/' }
    ]
}

/** The umbrella pages every footer links to. */
export const LEGAL_LINKS: FooterLink[] = [
    { href: '/about-us', en: 'About Suvaani' },
    { href: '/contact', en: 'Contact' },
    { href: '/privacy', en: 'Privacy' },
    { href: '/terms', en: 'Terms of use' }
]

export interface NavItem {
    href: string
    hi: string
    en: string
    match?: string[]
}

/** The Prakrit course's sections, in the header (English) and the bottom bar. */
export const PRIMARY_NAV: NavItem[] = [
    { href: '/prakrit', hi: 'गृहपृष्ठ', en: 'Home' },
    { href: '/learn', hi: 'सीखें', en: 'Learn', match: ['/learn'] },
    { href: '/practice', hi: 'अभ्यास', en: 'Practice', match: ['/practice'] },
    { href: '/grammar', hi: 'व्याकरण', en: 'Grammar', match: ['/grammar'] },
    { href: '/vocabulary', hi: 'शब्दकोश', en: 'Vocabulary', match: ['/vocabulary'] },
    { href: '/stories', hi: 'कथाएँ', en: 'Stories', match: ['/stories'] },
    { href: '/progress', hi: 'प्रगति', en: 'Progress', match: ['/progress'] }
]

export interface FooterLink {
    href: string
    en: string
}

/** The footer columns of each course (the footer is the same component in both). */
export const FOOTER_LINKS: Record<'prakrit' | 'sanskrit', { learn: FooterLink[], grammar: FooterLink[] }> = {
    prakrit: {
        learn: [
            { href: '/learn', en: 'Course map' },
            { href: '/learn/u2-pahla-vakya', en: 'Make your first sentence' },
            { href: '/practice', en: 'Practice and games' },
            { href: '/stories', en: 'Stories and dialogues' },
            { href: '/vocabulary', en: 'Vocabulary' },
            { href: '/about', en: 'About this course' }
        ],
        grammar: [
            { href: '/grammar/kriya', en: 'Verb forms' },
            { href: '/grammar/shabdarup', en: 'Declensions' },
            { href: '/grammar/vibhakti', en: 'Cases (vibhakti)' },
            { href: '/grammar/sandhi', en: 'Sandhi' },
            { href: '/about/content', en: 'Sources and checks' }
        ]
    },
    sanskrit: {
        learn: [
            { href: '/sanskrit/learn', en: 'Course map' },
            { href: '/sanskrit/learn/l15-first-sentence', en: 'Make your first sentence' },
            { href: '/sanskrit/practice', en: 'Practice and games' },
            { href: '/sanskrit/stories', en: 'Stories and dialogues' },
            { href: '/sanskrit/vocabulary', en: 'Vocabulary' }
        ],
        grammar: [
            { href: '/sanskrit/grammar/dhatu', en: 'Verb forms (dhātu)' },
            { href: '/sanskrit/grammar/shabdarupa', en: 'Declensions' },
            { href: '/sanskrit/grammar/vibhakti', en: 'Cases (vibhakti)' },
            { href: '/sanskrit/grammar/sandhi', en: 'Sandhi' },
            { href: '/sanskrit/about/content', en: 'Sources and verification' }
        ]
    }
}
